/**
 * ProgressIndicator Component
 * Displays progress bar for uploads and processing
 * 
 * Requirements: 15.2
 */

import './ProgressIndicator.css';

interface ProgressIndicatorProps {
  progress: number; // 0-100
  message?: string;
  showPercentage?: boolean;
  variant?: 'linear' | 'circular';
  size?: 'small' | 'medium' | 'large';
}

function ProgressIndicator({
  progress,
  message,
  showPercentage = true,
  variant = 'linear',
  size = 'medium',
}: ProgressIndicatorProps) {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  if (variant === 'circular') {
    const radius = size === 'small' ? 18 : size === 'large' ? 32 : 24;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clampedProgress / 100) * circumference;

    return (
      <div className={`progress-indicator progress-circular progress-${size}`}>
        <svg className="circular-progress" viewBox="0 0 64 64">
          <circle
            className="circular-progress-bg"
            cx="32"
            cy="32"
            r={radius}
          />
          <circle
            className="circular-progress-bar"
            cx="32"
            cy="32"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        {showPercentage && (
          <span className="circular-progress-text">{Math.round(clampedProgress)}%</span>
        )}
      </div>
    );
  }

  return (
    <div className={`progress-indicator progress-linear progress-${size}`}>
      {message && <p className="progress-message">{message}</p>}
      <div className="progress-bar-container">
        <div
          className="progress-bar"
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {showPercentage && clampedProgress > 10 && (
            <span className="progress-percentage">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      </div>
      {showPercentage && clampedProgress <= 10 && (
        <span className="progress-percentage-external">{Math.round(clampedProgress)}%</span>
      )}
    </div>
  );
}

export default ProgressIndicator;
